import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Heart, SlidersHorizontal, X, ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { name: 'Ring', type: 'ring', image: 'https://images.unsplash.com/photo-1702721024321-20ab7b86a5d0?w=600&q=80' },
  { name: 'Earring', type: 'earring', image: 'https://images.unsplash.com/photo-1632525231035-c054cd5019db?w=600&q=80' },
  { name: 'Pendant', type: 'pendant', image: 'https://images.unsplash.com/photo-1596187262703-c13003fbfac1?w=600&q=80' },
  { name: 'Bracelet', type: 'bracelet', image: 'https://images.unsplash.com/photo-1605884878538-6468614df578?w=600&q=80' },
  { name: 'Mangalsutra', type: 'mangalsutra', image: 'https://images.unsplash.com/photo-1750492272603-43888a3fee4d?w=600&q=80' },
  { name: 'Necklace', type: 'necklace', image: 'https://images.unsplash.com/photo-1758995115518-26f90aa61b97?w=600&q=80' },
];

const METAL_SWATCHES = [
  { key: 'yellow', color: '#FFD700', label: 'Yellow Gold' },
  { key: 'rose', color: '#E8A87C', label: 'Rose Gold' },
  { key: 'white', color: '#E0E0E0', label: 'White Gold' },
  { key: 'silver', color: '#C0C0C0', label: 'Silver' },
];

const BADGES = ['Best Seller', 'Most Loved', 'New Arrival', 'Trending'];

const CategoryGrid = ({ category }) => {
  // Build sub-link respecting current category filter
  const buildLink = (type) => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    params.set('type', type);
    return `/products?${params.toString()}`;
  };
  const heading = category
    ? `Shop ${category.charAt(0).toUpperCase() + category.slice(1)} by Category`
    : 'Shop by Category';
  return (
    <div className="min-h-screen bg-[#FDFBF7] pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-16">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#7A8A73] mb-3 md:mb-4">Browse By Category</p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif tracking-tight text-[#2D3B26] leading-[1.1]" data-testid="category-grid-heading">{heading}</h1>
          {category && (
            <Link to={`/products?category=${category}&type=all`} data-testid="view-all-category" className="inline-block mt-4 text-xs uppercase tracking-widest text-[#C27054] font-semibold hover:underline">
              View All {category.charAt(0).toUpperCase() + category.slice(1)} →
            </Link>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
          {CATEGORIES.map((cat, index) => (
            <Link to={buildLink(cat.type)} key={cat.type} data-testid={`category-tile-${cat.type}`}>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative aspect-[4/5] overflow-hidden rounded-2xl group cursor-pointer bg-[#E4E8DF]">
                <img src={cat.image} alt={cat.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2D3B26]/70 via-transparent to-transparent flex items-end justify-center pb-4 md:pb-8">
                  <h3 className="text-base md:text-xl lg:text-2xl font-serif text-[#FDFBF7] tracking-wide">{cat.name}</h3>
                </div>
              </motion.div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

const FilterSection = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[rgba(45,59,38,0.08)] py-4">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full text-left">
        <span className="text-sm font-semibold text-[#2D3B26] uppercase tracking-wider">{title}</span>
        {open ? <ChevronUp size={16} className="text-[#7A8A73]" /> : <ChevronDown size={16} className="text-[#7A8A73]" />}
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
};

const ProductCard = ({ product, onWishlist, index }) => {
  const [liked, setLiked] = useState(false);
  const badge = product.price > 3000 ? BADGES[0] : product.price > 2000 ? BADGES[1] : null;
  const swatches = product.category === 'gold' ? METAL_SWATCHES.slice(0, 3) : METAL_SWATCHES.slice(2);

  // "Newly Listed" if available_date is within the last 7 days
  const isNewlyListed = (() => {
    if (!product.available_date) return false;
    const listed = new Date(product.available_date);
    const today = new Date(); today.setHours(0,0,0,0);
    const diffDays = Math.floor((today - listed) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 7;
  })();

  return (
    <Link to={`/product/${product.id}`} data-testid={`product-card-${product.id}`}>
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="group">
        <div className="relative bg-white rounded-lg overflow-hidden hover:shadow-[0_8px_30px_rgba(45,59,38,0.12)] transition-all duration-300">
          {/* Badge */}
          {(isNewlyListed || badge) && (
            <div className="absolute top-3 left-3 z-10 flex flex-col gap-1">
              {isNewlyListed && (
                <span data-testid={`newly-listed-${product.id}`} className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded bg-[#C27054] text-white">
                  ✨ New
                </span>
              )}
              {badge && (
                <span className="text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded bg-[#2D3B26]/90 text-white">{badge}</span>
              )}
            </div>
          )}
          {/* Heart */}
          <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLiked(!liked); onWishlist(product.id); }}
            data-testid={`wishlist-btn-${product.id}`}
            className="absolute top-3 right-3 z-10">
            <Heart size={20} className={`transition-colors ${liked ? 'fill-[#C27054] text-[#C27054]' : 'text-[#b0b0b0] hover:text-[#C27054]'}`} />
          </button>

          {/* Image */}
          <div className="aspect-square bg-[#fafaf8] flex items-center justify-center p-4 overflow-hidden relative">
            <img src={product.image_url} alt={product.name}
              className={`w-full h-full object-contain transition-transform duration-500 group-hover:scale-110 ${product.in_stock === false ? 'opacity-60 grayscale' : ''}`} />
            {product.in_stock === false && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span data-testid={`out-of-stock-${product.id}`} className="text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded bg-[#2D3B26] text-white shadow-lg">
                  Out of Stock
                </span>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="px-4 pt-3 pb-4">
            {/* Swatches */}
            <div className="flex items-center gap-1 mb-2">
              {swatches.map((s) => (
                <div key={s.key} className="w-3.5 h-3.5 rounded-full border border-[rgba(0,0,0,0.12)]" style={{ backgroundColor: s.color }} title={s.label} />
              ))}
            </div>
            <h3 className="text-[13px] font-sans text-[#2D3B26] leading-snug mb-1 group-hover:text-[#C27054] transition-colors line-clamp-2 font-medium">
              {product.name}
            </h3>
            <p className="text-sm font-semibold text-[#2D3B26]">{'\u20b9'}{product.price.toLocaleString('en-IN')}</p>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const category = searchParams.get('category');
  const search = searchParams.get('search');
  const jewelryTypeRaw = searchParams.get('type');
  const jewelryType = jewelryTypeRaw === 'all' ? null : jewelryTypeRaw;
  const maxPrice = searchParams.get('max_price');
  const minPrice = searchParams.get('min_price');
  const maxWeight = searchParams.get('max_weight');
  const minWeight = searchParams.get('min_weight');
  const gift = searchParams.get('gift');
  const gender = searchParams.get('gender');
  const collection = searchParams.get('collection');
  const sortBy = searchParams.get('sort') || '';
  // Listing shows when ANY of these filters is set (except bare category — that shows CategoryGrid)
  const hasListingFilters = search || jewelryType || maxPrice || minPrice || maxWeight || minWeight || gift || gender || collection || jewelryTypeRaw === 'all';
  const hasFilters = category || hasListingFilters;

  useEffect(() => {
    if (hasListingFilters || (category && jewelryType)) fetchProducts();
    else setLoading(false);
  }, [category, search, jewelryType, maxPrice, minPrice, maxWeight, minWeight, gift, gender, collection, sortBy]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (category) params.category = category;
      if (search) params.search = search;
      if (jewelryType) params.type = jewelryType;
      if (maxPrice) params.max_price = maxPrice;
      if (gift && gift !== 'true') params.gift = gift;
      if (gender) params.gender = gender;
      if (collection) params.collection = collection;
      const { data } = await axios.get(`${BACKEND_URL}/api/products`, { params, withCredentials: true });

      // Client-side filters + sort
      let result = [...data];
      if (minPrice) result = result.filter((p) => Number(p.price || 0) >= Number(minPrice));
      if (minWeight) result = result.filter((p) => Number(p.weight_grams || 0) >= Number(minWeight));
      if (maxWeight) result = result.filter((p) => Number(p.weight_grams || 0) <= Number(maxWeight));

      if (sortBy === 'price_low') result.sort((a, b) => (a.price || 0) - (b.price || 0));
      else if (sortBy === 'price_high') result.sort((a, b) => (b.price || 0) - (a.price || 0));
      else if (sortBy === 'new') result.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      else if (sortBy === 'old') result.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
      else if (sortBy === 'name') result.sort((a, b) => a.name.localeCompare(b.name));

      setProducts(result);
    } catch (error) { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  };

  const addToWishlist = async (productId) => {
    if (!user) { toast.error('Please login to add to wishlist'); return; }
    try { await axios.post(`${BACKEND_URL}/api/wishlist`, { product_id: productId }, { withCredentials: true }); toast.success('Added to wishlist!'); }
    catch { toast.error('Failed to add to wishlist'); }
  };

  const setFilter = (key, val) => {
    const p = new URLSearchParams(searchParams);
    if (val) p.set(key, val); else p.delete(key);
    setSearchParams(p);
  };

  const handleSort = (val) => {
    setFilter('sort', val || null);
  };

  if (!hasListingFilters && !category) return <CategoryGrid />;
  if (category && !hasListingFilters) return <CategoryGrid category={category} />;

  const getTitle = () => {
    if (collection) return collection.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    if (gender) return `${gender.charAt(0).toUpperCase() + gender.slice(1)}'s Jewelry`;
    if (gift === 'him') return 'Gifts for Him';
    if (gift === 'her') return 'Gifts for Her';
    if (gift === 'true') return 'Gift Collection';
    if (maxPrice) return `Under \u20b9${Number(maxPrice).toLocaleString('en-IN')}`;
    if (search) return `Results for "${search}"`;
    if (jewelryType) return `${jewelryType.charAt(0).toUpperCase() + jewelryType.slice(1)}s`;
    if (category) return `${category.charAt(0).toUpperCase() + category.slice(1)} Jewelry`;
    return 'All Jewelry';
  };

  const activeFilters = [];
  if (category) activeFilters.push({ key: 'category', label: category.charAt(0).toUpperCase() + category.slice(1) });
  if (jewelryType) activeFilters.push({ key: 'type', label: jewelryType.charAt(0).toUpperCase() + jewelryType.slice(1) });
  if (gender) activeFilters.push({ key: 'gender', label: gender.charAt(0).toUpperCase() + gender.slice(1) });
  if (collection) activeFilters.push({ key: 'collection', label: collection.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) });
  if (maxPrice) activeFilters.push({ key: 'max_price', label: `Under \u20b9${Number(maxPrice).toLocaleString('en-IN')}` });
  if (maxWeight) activeFilters.push({ key: 'max_weight', label: `Under ${maxWeight}g` });
  if (minWeight) activeFilters.push({ key: 'min_weight', label: `Above ${minWeight}g` });

  return (
    <div className="min-h-screen bg-[#FDFBF7] pt-24 lg:pt-28">
      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-6 py-3 border-b border-[rgba(45,59,38,0.08)]">
        <div className="flex items-center gap-2 text-xs text-[#7A8A73]">
          <Link to="/" className="hover:text-[#C27054] transition-colors">Home</Link>
          <span>/</span>
          <Link to="/products" className="hover:text-[#C27054] transition-colors">Jewelry</Link>
          {jewelryType && <><span>/</span><span className="text-[#2D3B26] font-medium">{jewelryType.charAt(0).toUpperCase() + jewelryType.slice(1)}s</span></>}
          {category && <><span>/</span><span className="text-[#2D3B26] font-medium">{category.charAt(0).toUpperCase() + category.slice(1)}</span></>}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 flex">
        {/* Filter Content (shared between sidebar + drawer) */}
        {(() => {
          const FilterBody = () => (
            <>
              <div className="flex items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal size={18} className="text-[#2D3B26]" />
                  <span className="text-sm font-bold text-[#2D3B26] uppercase tracking-wider">Filters</span>
                </div>
                <button
                  onClick={() => setMobileFilterOpen(false)}
                  className="lg:hidden text-[#7A8A73] hover:text-[#2D3B26]"
                  data-testid="close-mobile-filter"
                >
                  <X size={20} />
                </button>
              </div>

              {activeFilters.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {activeFilters.map((f) => (
                    <button key={f.key} onClick={() => setFilter(f.key, null)} data-testid={`remove-filter-${f.key}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#E4E8DF] text-[#2D3B26] text-xs font-medium rounded-full hover:bg-[#C27054] hover:text-white transition-colors">
                      {f.label} <X size={10} />
                    </button>
                  ))}
                </div>
              )}

              <FilterSection title="Metal">
                <div className="space-y-2">
                  {['Gold', 'Silver'].map((m) => (
                    <label key={m} className="flex items-center gap-2.5 cursor-pointer group">
                      <input type="radio" name="metal" checked={category === m.toLowerCase()} onChange={() => setFilter('category', m.toLowerCase())}
                        className="w-4 h-4 text-[#C27054] border-[#7A8A73] focus:ring-[#C27054]" />
                      <span className="text-sm text-[#475740] group-hover:text-[#C27054] transition-colors">{m}</span>
                    </label>
                  ))}
                  <button onClick={() => setFilter('category', null)} className="text-xs text-[#C27054] hover:underline mt-1">Clear</button>
                </div>
              </FilterSection>

              <FilterSection title="Price">
                <div className="space-y-2">
                  {[{ label: 'Under \u20b95,000', val: '5000' }, { label: 'Under \u20b910,000', val: '10000' }, { label: 'Under \u20b915,000', val: '15000' }, { label: 'Under \u20b925,000', val: '25000' }, { label: 'Under \u20b930,000', val: '30000' }, { label: 'Under \u20b950,000', val: '50000' }].map((p) => (
                    <label key={p.val} className="flex items-center gap-2.5 cursor-pointer group">
                      <input type="radio" name="price" checked={maxPrice === p.val} onChange={() => setFilter('max_price', p.val)}
                        className="w-4 h-4 text-[#C27054] border-[#7A8A73] focus:ring-[#C27054]" />
                      <span className="text-sm text-[#475740] group-hover:text-[#C27054] transition-colors">{p.label}</span>
                    </label>
                  ))}
                  <button onClick={() => setFilter('max_price', null)} className="text-xs text-[#C27054] hover:underline mt-1">Clear</button>
                </div>
              </FilterSection>

              <FilterSection title="Weight (grams)">
                <div className="space-y-2">
                  {[
                    { label: 'Under 2g', val: '2' },
                    { label: 'Under 5g', val: '5' },
                    { label: 'Under 10g', val: '10' },
                    { label: 'Under 20g', val: '20' },
                    { label: 'Above 20g', val: 'above_20' },
                  ].map((w) => (
                    <label key={w.val} className="flex items-center gap-2.5 cursor-pointer group">
                      <input
                        type="radio"
                        name="weight"
                        checked={(w.val === 'above_20' && minWeight === '20' && !maxWeight) || (w.val !== 'above_20' && maxWeight === w.val && !minWeight)}
                        onChange={() => {
                          if (w.val === 'above_20') {
                            setFilter('min_weight', '20');
                            setFilter('max_weight', null);
                          } else {
                            setFilter('max_weight', w.val);
                            setFilter('min_weight', null);
                          }
                        }}
                        data-testid={`weight-${w.val}`}
                        className="w-4 h-4 text-[#C27054] border-[#7A8A73] focus:ring-[#C27054]"
                      />
                      <span className="text-sm text-[#475740] group-hover:text-[#C27054] transition-colors">{w.label}</span>
                    </label>
                  ))}
                  <button
                    onClick={() => { setFilter('max_weight', null); setFilter('min_weight', null); }}
                    data-testid="weight-clear"
                    className="text-xs text-[#C27054] hover:underline mt-1"
                  >
                    Clear
                  </button>
                </div>
              </FilterSection>

              <FilterSection title="Type">
                <div className="space-y-2">
                  {CATEGORIES.map((c) => (
                    <label key={c.type} className="flex items-center gap-2.5 cursor-pointer group">
                      <input type="radio" name="type" checked={jewelryType === c.type} onChange={() => setFilter('type', c.type)}
                        className="w-4 h-4 text-[#C27054] border-[#7A8A73] focus:ring-[#C27054]" />
                      <span className="text-sm text-[#475740] group-hover:text-[#C27054] transition-colors">{c.name}</span>
                    </label>
                  ))}
                  <button onClick={() => setFilter('type', null)} className="text-xs text-[#C27054] hover:underline mt-1">Clear</button>
                </div>
              </FilterSection>
            </>
          );

          return (
            <>
              {/* Desktop Sidebar */}
              <aside className={`hidden lg:block w-60 flex-shrink-0 pr-8 py-6 ${sidebarOpen ? '' : 'lg:hidden'}`}>
                <div className="sticky top-32">
                  <FilterBody />
                </div>
              </aside>

              {/* Mobile Filter Drawer */}
              {mobileFilterOpen && (
                <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-filter-drawer">
                  <div className="flex-1 bg-black/40" onClick={() => setMobileFilterOpen(false)} />
                  <motion.div
                    initial={{ x: '100%' }}
                    animate={{ x: 0 }}
                    transition={{ duration: 0.25 }}
                    className="w-[85%] max-w-sm bg-[#FDFBF7] h-full overflow-y-auto p-5"
                  >
                    <FilterBody />
                    <button
                      onClick={() => setMobileFilterOpen(false)}
                      data-testid="apply-mobile-filter"
                      className="mt-6 w-full bg-[#C27054] text-white rounded-full px-6 py-3 text-sm uppercase tracking-widest font-semibold hover:bg-[#A85B42] transition-colors"
                    >
                      Show {products.length} Results
                    </button>
                  </motion.div>
                </div>
              )}
            </>
          );
        })()}

        {/* Main Content */}
        <main className="flex-1 py-6 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 gap-3">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl md:text-2xl font-serif text-[#2D3B26] mb-1 truncate">{getTitle()}</h1>
              <p className="text-xs md:text-sm text-[#7A8A73]">{products.length} Results</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMobileFilterOpen(true)}
                data-testid="mobile-filter-button"
                className="lg:hidden flex items-center gap-1.5 text-sm text-[#475740] bg-white border border-[rgba(45,59,38,0.15)] rounded-lg px-3 py-2 hover:border-[#C27054]"
              >
                <SlidersHorizontal size={14} />
                <span>Filters{activeFilters.length > 0 ? ` (${activeFilters.length})` : ''}</span>
              </button>
              <select onChange={(e) => handleSort(e.target.value)} value={sortBy} data-testid="sort-select"
                className="text-xs md:text-sm text-[#475740] bg-transparent border border-[rgba(45,59,38,0.15)] rounded-lg px-2 md:px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#C27054]">
                <option value="">Sort By</option>
                <option value="new">New to Old</option>
                <option value="old">Old to New</option>
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
                <option value="name">Name: A to Z</option>
              </select>
            </div>
          </div>

          {/* Loading */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C27054]"></div>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#7A8A73] text-lg mb-4">No products found</p>
              <Link to="/products" className="text-[#C27054] hover:underline text-sm">Browse all categories</Link>
            </div>
          ) : (
            /* Product Grid - 4 columns like Brilliant Earth */
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map((product, index) => (
                <ProductCard key={product.id} product={product} onWishlist={addToWishlist} index={index} />
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Products;
